import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Star, AlertTriangle, Target, Eye, Zap, Clock } from 'lucide-react';
import { useCommandStore } from '../../store/commandStore.js';
import StatCounter from './StatCounter.js';

interface BriefingCardProps {
  places: import('@wingman/shared').Place[];
}

export default function BriefingCard({ places }: BriefingCardProps) {
  const { briefingData, sessionStats, setBriefingData, setSessionStats } = useCommandStore();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (places.length === 0) {
      setBriefingData(null);
      setSessionStats({ detected: 0, openNow: 0 });
      return;
    }
    import('../../lib/briefing.js').then(({ computeAreaBriefing, computeSessionStats }) => {
      const briefing = computeAreaBriefing(places);
      setBriefingData(briefing);
      setSessionStats({
        detected: briefing.totalCount,
        openNow: briefing.openCount,
      });
    });
  }, [places, setBriefingData, setSessionStats]);

  if (!briefingData || briefingData.totalCount === 0) return null;

  const missionStatus = briefingData.openCount > 0 ? 'MISSION READY' : 'SECTOR CLOSED';
  const statusTone = briefingData.openCount > 0 ? 'green' : 'amber';

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.28 }}
      className="briefing-card"
    >
      <div className="briefing-card-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="briefing-card-title">
          <Target size={12} />
          <span className="briefing-card-eyebrow">SECTOR ANALYSIS</span>
        </div>
        <div className="briefing-card-status-group">
          <span className={`briefing-card-status briefing-card-status-${statusTone}`}>
            {missionStatus}
          </span>
          <button type="button" className="briefing-card-collapse-btn">
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="briefing-card-body"
          >
            <div className="briefing-card-stats">
              <StatCounter icon={Eye} label="DETECTED" value={sessionStats.detected} tone="cyan" />
              <StatCounter icon={Zap} label="OPEN NOW" value={sessionStats.openNow} tone="green" />
              <StatCounter icon={Clock} label="CLOSING" value={briefingData.closingSoonCount} tone="amber" />
            </div>

            {briefingData.topRated.length > 0 && (
              <div className="briefing-section">
                <div className="briefing-section-label">TOP RATED</div>
                {briefingData.topRated.map((place) => (
                  <div key={place.id} className="briefing-top-rated-row">
                    <Star size={10} className="briefing-star" />
                    <span className="briefing-top-rated-name">{place.name}</span>
                    <span className="briefing-top-rated-rating">{place.rating.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}

            {briefingData.categoryBreakdown.length > 0 && (
              <div className="briefing-section">
                <div className="briefing-section-label">CATEGORY BREAKDOWN</div>
                {briefingData.categoryBreakdown.map((cat) => (
                  <div key={cat.slug} className="briefing-category-row">
                    <span className="briefing-category-name">{cat.name}</span>
                    <div className="briefing-category-bar-container">
                      <div
                        className="briefing-category-bar"
                        style={{
                          width: `${Math.min(100, (cat.count / briefingData.totalCount) * 100)}%`,
                          background: cat.color,
                        }}
                      />
                    </div>
                    <span className="briefing-category-count">{cat.count}</span>
                  </div>
                ))}
              </div>
            )}

            {briefingData.watchFor.length > 0 && (
              <div className="briefing-section">
                <div className="briefing-section-label">
                  <AlertTriangle size={10} /> WATCH FOR
                </div>
                {briefingData.watchFor.map((warning, i) => (
                  <div key={i} className="briefing-watch-for-row">
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
